import React, { createContext, useContext } from 'react';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { getSoftDepthTheme } from './theme';
import type { SoftDepthMode } from './tokens';

type SoftDepthContextValue = {
  mode: SoftDepthMode;
  /** The theme that was in force outside the outermost scope. */
  outerTheme: Theme;
};

/**
 * What the nearest SoftDepthScope knows, kept apart from the MUI theme so it
 * survives a LegacyThemeScope in between: a Soft Depth control handed to a
 * legacy component (and rendered back inside it) re-enters in the right mode,
 * and a LegacyThemeScope puts back exactly the theme the scope replaced.
 */
export const SoftDepthContext = createContext<SoftDepthContextValue | null>(null);

export interface SoftDepthScopeProps {
  /**
   * 'recruit' swaps the accent family to amber and warms the page ground.
   * Omitted, it inherits the nearest outer scope's mode (else 'business').
   */
  mode?: SoftDepthMode;
  children: React.ReactNode;
}

/**
 * Everything inside renders with the Soft Depth MUI theme; everything outside
 * keeps whatever theme it had. Portalled MUI parts (Menu, Dialog, Popover)
 * opened from inside follow it too, because the theme travels by React
 * context, not by DOM position — unlike the old `.clientv2-page[data-mode]`
 * CSS variables, which never reached them.
 */
const SoftDepthScope = ({ mode, children }: SoftDepthScopeProps) => {
  const outer = useContext(SoftDepthContext);
  const currentTheme = useTheme();
  const value: SoftDepthContextValue = {
    mode: mode ?? outer?.mode ?? 'business',
    // Nested scopes keep the outermost one's: the theme just above a nested
    // scope is Soft Depth itself, or a LegacyThemeScope's copy of the same.
    outerTheme: outer?.outerTheme ?? currentTheme
  };
  return (
    <SoftDepthContext.Provider value={value}>
      <ThemeProvider theme={getSoftDepthTheme(value.mode)}>{children}</ThemeProvider>
    </SoftDepthContext.Provider>
  );
};

export default SoftDepthScope;
